import React, { useState } from 'react';
import { Dropdown } from 'antd';
import type { DropdownProps } from 'antd';
import type { AgentOption } from '../types';
import { rteSelectPopupContainer } from '../utils/selectPlacements';

// 智能体下拉自适应翻转：placement=bottomLeft 默认向下；下方空间不足时向上翻转、始终锚定按钮。
// adjustY 开启翻转（下方放不下→翻到按钮上方），adjustX:1 左右越界微调。
// ⚠️ 关键：shiftY 必须为 false。早期为「彻底出视口时平移贴边」开了 shiftY:true，但触发器在
// 编辑器底部、菜单又高时，下方放不下 + 没开 adjustY 翻转 → rc-trigger 走 shiftY 把整个菜单
// 平移进视口 → 菜单被推到视口左上角、脱离按钮（用户报「下方距离不够时跑到左上角」）。
// 正解是用 adjustY 翻转（菜单整体翻到按钮上方、保持锚定），而非 shiftY 平移（会和触发器脱节）。
// ⚠️ 翻转方向要算对，还得配合 getPopupContainer=document.body（见下方 rteSelectPopupContainer）：
// 否则浮层挂 .rte-popup-host、被消费端滚动祖先裁剪可视空间 → 即使下方有空间也总朝上翻
// （与字段下拉同根因，next.79 一并修）。
const AGENT_DROPDOWN_ADJUST = {
  adjustX: 1,
  adjustY: 1,
  shiftX: false,
  shiftY: false,
} as unknown as DropdownProps['autoAdjustOverflow'];


type Props = {
  options: AgentOption[];
  onSelect?: (agent: AgentOption) => void;
  disabled?: boolean;
  /** 当前选中智能体（由 Footer 透传）→ 下拉菜单 selectedKeys 高亮选中项（V1 parity） */
  currentAgent?: AgentOption;
  /** 下拉展开状态变化回调（用于外部在打开瞬间把焦点拉回输入框，渲染选中态） */
  onOpenChange?: (open: boolean) => void;
  /** 自定义对齐（rc-trigger align）。默认走 placement=bottomLeft 向下展开；
   *  折叠进「更多」菜单时传右侧对齐，让二级菜单从右侧级联展开、不遮住「更多」列表
   *  （antd Dropdown 的 placement 不支持 right*，故用 align 手动定位）。 */
  align?: DropdownProps['align'];
  /** 选中态右侧 × 退出智能体（与编辑器内智能体 tag 关闭同一路径）。 */
  onExit?: () => void;
};

export const AgentDropdown: React.FC<Props> = React.memo(({ options, onSelect, disabled, currentAgent, onOpenChange, align, onExit }) => {
  // 下拉是否展开：用于「点击/打开 = 紫色无边框」的态（与「已选 = 紫色有边框」区分）
  const [open, setOpen] = useState(false);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const items = options
    .filter((o) => o.isShowNew !== 1)
    .map((o) => ({
      key: o.key,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {o.icon && <i className={o.icon} />}
          <span>{o.label}</span>
        </div>
      ),
      onClick: () => onSelect?.(o),
    }));

  // 选中态优先：已选智能体 → 紫底紫边；仅展开未选 → 紫底无边框（active）
  const selected = !!currentAgent;
  const badgeClass = `rte-agent-badge${selected ? ' rte-agent-badge-selected' : open ? ' rte-agent-badge-active' : ''}`;

  return (
    <Dropdown
      menu={{ items, selectedKeys: currentAgent ? [String(currentAgent.key)] : [] }}
      disabled={disabled}
      placement="bottomLeft"
      align={align}
      autoAdjustOverflow={AGENT_DROPDOWN_ADJUST}
      getPopupContainer={rteSelectPopupContainer}
      trigger={['click']}
      overlayClassName="rte-agent-dropdown"
      onOpenChange={handleOpenChange}
    >
      <div
        className={badgeClass}
        style={{ cursor: options.length > 0 ? 'pointer' : 'default' }}
      >
        <i className="iconfont icon-zhinengti" style={{ fontSize: '16px' }} />
        <span className="rte-agent-text">智能体</span>
        {/* 选中态右侧 × 退出智能体。stopPropagation 防止冒泡触发 Dropdown 打开菜单。 */}
        {selected && onExit && (
          <i
            className="iconfont icon-guanbi rte-tool-close"
            role="button"
            aria-label="退出智能体"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (disabled) return;
              onExit();
            }}
          />
        )}
      </div>
    </Dropdown>
  );
});
AgentDropdown.displayName = 'AgentDropdown';
